// What the screen and the server share about datasets: the shapes, the limits
// and the check of rows. Runs in the browser too: imports nothing from node.
//
// Promise:
// - A batch of writes is checked whole: one bad row refuses all of it, and
//   the errors name the row (its index in the batch) and the field.
// - A row's `_id` comes from the schema's key fields when it has a key (the
//   same values give the same id here and in the browser), else the server
//   makes one.
// - Values are kept as written: a schema's types only accept or refuse them.

export type DatasetFieldType =
  | "number"
  | "string"
  | "date"
  | "datetime"
  | "boolean"
  | "json";

export const DATASET_FIELD_TYPES: readonly DatasetFieldType[] = [
  "number",
  "string",
  "date",
  "datetime",
  "boolean",
  "json",
];

export interface DatasetField {
  name: string;
  type: DatasetFieldType;
  unit?: string;
  label?: string;
}

export interface DatasetSchema {
  fields: DatasetField[];
  /** Fields whose values name a row: writing the same values replaces it. */
  key?: string[];
}

/** A stored row: what was written, with its id and when it was written. */
export type DatasetRow = Record<string, unknown> & { _id: string; _at: string };

export type DatasetWriteOp =
  | { op: "append"; row: Record<string, unknown> }
  | { op: "upsert"; row: Record<string, unknown> }
  | { op: "remove"; id: string };

export const DATASET_LIMITS = {
  /** Rows a dataset holds. */
  rows: 10_000,
  /** Bytes of its rows as JSON (UTF-8, with `_id` and `_at`). */
  bytes: 10_000_000,
  /** Bytes of one row as JSON (with `_id` and `_at`). */
  rowBytes: 8_192,
  /** Operations in one write. */
  opsPerWrite: 1_000,
  /** Rows a query result hands to a frame. */
  toFrame: 5_000,
  /** Operations after which the dataset is packed again. */
  compactOps: 500,
  /** Packed again this long after the last packing when anything changed. */
  compactAgeMs: 86_400_000,
  /** Packed operations are kept this long for screens still behind them. */
  opsKeepMs: 7 * 86_400_000,
  /** Operations one sync hands out. */
  syncOps: 5_000,
  /** Fields of a schema. */
  fields: 100,
  /** Retention in days. */
  retentionDays: 3_650,
} as const;

export const DATASET_TITLE_MAX = 200;
export const DATASET_ID_MAX = 200;

export type DatasetPlace =
  | { kind: "space"; workspace: string; spaceId: string; spaceName: string }
  | { kind: "personal" };

/** Where the person's own datasets are listed (beside the personal documents). */
export const ME_DATASETS_PATH = "/me/documents/datasets";
/** The screen of a personal dataset (a pasted URL of it becomes a reference). */
export const ME_DATASET_REF_PATHS = [`${ME_DATASETS_PATH}/:id`] as const;
export const meDatasetPath = (id: string) => `${ME_DATASETS_PATH}/${id}`;

export interface DatasetDTO {
  id: string;
  title: string;
  place: DatasetPlace;
  schema: DatasetSchema | null;
  retentionDays: number | null;
  retentionField: string | null;
  timezone: string;
  seq: number;
  rev: number;
  rowCount: number;
  byteSize: number;
  /**
   * Personal data moved here while it had write tokens where it came from
   * (revoked there): new ones are to be issued here. Off once one is.
   */
  tokensLeft: boolean;
  createdAt: string;
  updatedAt: string;
}

export type SyncOp =
  | { seq: number; put: DatasetRow }
  | { seq: number; delete: string };

/**
 * What a screen gets for `sync?since=&rev=`: only `seq` when nothing changed;
 * `meta` when its `rev` is old; the operations after `since`, or `reset` (load
 * the snapshot at `url`, then apply `ops`) when `since` is older than what is
 * kept; `more` when there are more operations than one answer carries.
 */
export interface DatasetSync {
  seq: number;
  meta?: DatasetDTO;
  ops?: SyncOp[];
  reset?: { url: string | null; seq: number };
  more?: true;
}

/**
 * A query worked out where the dataset lives (`POST /api/datasets/:id/query`):
 * the result only, at most `DATASET_LIMITS.toFrame` rows, at `seq`.
 */
export interface DatasetQueryAnswer {
  seq: number;
  dataset: DatasetDTO;
  rows: Record<string, unknown>[];
  truncated: boolean;
}

export interface DatasetWriteResult {
  seq: number;
  rows: number;
  bytes: number;
}

/** A write token as listed (the token itself only in the answer that issued it). */
export interface DatasetTokenDTO {
  id: string;
  name: string;
  createdAt: string;
  lastUsedAt: string | null;
}

export type RowErrorReason =
  | "type"
  | "unknown_field"
  | "too_large"
  | "not_object"
  | "missing_key";

export interface RowError {
  index: number;
  field?: string;
  reason: RowErrorReason;
}

export type RowCheck = { ok: true } | { ok: false; errors: RowError[] };

const MAX_ERRORS = 20;
const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DATETIME_RE =
  /^(\d{4})-(\d{2})-(\d{2})T\d{2}:\d{2}(:\d{2}(\.\d{1,9})?)?(Z|[+-]\d{2}:\d{2})$/;
const FIELD_NAME_RE = /^[^_\s][^\s]{0,63}$/;
/** Stand-ins of the `_id` and `_at` the server adds, to size a row before. */
const ID_STAND_IN = "00000000-0000-0000-0000-000000000000";
const AT_STAND_IN = "2026-01-01T00:00:00.000Z";

const encoder = new TextEncoder();

/** Bytes of a value as JSON (UTF-8). */
export function jsonBytes(value: unknown): number {
  return encoder.encode(JSON.stringify(value)).length;
}

function isDate(value: unknown): boolean {
  if (typeof value !== "string") return false;
  const m = DATE_RE.exec(value);
  if (!m) return false;
  const d = new Date(`${value}T00:00:00Z`);
  return (
    !Number.isNaN(d.getTime()) &&
    d.getUTCFullYear() === Number(m[1]) &&
    d.getUTCMonth() + 1 === Number(m[2]) &&
    d.getUTCDate() === Number(m[3])
  );
}

function isDatetime(value: unknown): boolean {
  if (typeof value !== "string") return false;
  const m = DATETIME_RE.exec(value);
  if (!m) return false;
  return isDate(`${m[1]}-${m[2]}-${m[3]}`) && !Number.isNaN(Date.parse(value));
}

/** Whether `value` fits `type` (null and missing values always fit). */
export function fitsType(type: DatasetFieldType, value: unknown): boolean {
  if (value === null || value === undefined) return true;
  switch (type) {
    case "number":
      return typeof value === "number" && Number.isFinite(value);
    case "string":
      return typeof value === "string";
    case "boolean":
      return typeof value === "boolean";
    case "date":
      return isDate(value);
    case "datetime":
      return isDatetime(value);
    case "json":
      return true;
  }
}

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

/** The row's id from the schema's key fields, or null without a key. */
export function rowIdOf(
  schema: DatasetSchema | null,
  row: Record<string, unknown>,
): string | null {
  const key = schema?.key;
  if (!key || key.length === 0) return null;
  return JSON.stringify(key.map((k) => row[k] ?? null));
}

/** The types a guessed field may take, the narrowest first. */
const GUESSED: readonly DatasetFieldType[] = [
  "date",
  "datetime",
  "number",
  "boolean",
  "string",
];

/**
 * Fields guessed from rows (a dataset without a schema): every name but `_id`
 * and `_at`, in the order first seen, typed by the narrowest type all their
 * values fit (null and missing values fit any); `json` when none does.
 */
export function inferSchema(rows: Iterable<DatasetRow>): DatasetSchema {
  // The types still possible; none seen yet (only nulls) is json.
  const types = new Map<string, DatasetFieldType[] | null>();
  for (const row of rows)
    for (const [name, value] of Object.entries(row)) {
      if (name === "_id" || name === "_at") continue;
      const left = types.get(name) ?? null;
      if (value === null || value === undefined) types.set(name, left);
      else
        types.set(
          name,
          (left ?? GUESSED).filter((t) => fitsType(t, value)),
        );
    }
  return {
    fields: [...types].map(([name, left]) => ({
      name,
      type: left?.[0] ?? "json",
    })),
  };
}

/**
 * Checks a batch of writes against the schema (null: any object). The first
 * 20 errors are kept.
 */
export function checkRows(
  schema: DatasetSchema | null,
  ops: readonly DatasetWriteOp[],
): RowCheck {
  const errors: RowError[] = [];
  const fields = new Map((schema?.fields ?? []).map((f) => [f.name, f]));
  for (const [index, op] of ops.entries()) {
    if (errors.length >= MAX_ERRORS) break;
    const error = opError(schema, fields, op);
    if (error) errors.push({ index, ...error });
  }
  return errors.length ? { ok: false, errors } : { ok: true };
}

const isRowId = (v: unknown) =>
  typeof v === "string" && v.length > 0 && v.length <= DATASET_ID_MAX;

function opError(
  schema: DatasetSchema | null,
  fields: Map<string, DatasetField>,
  op: DatasetWriteOp,
): Omit<RowError, "index"> | null {
  const key = schema?.key?.length ? schema.key : null;
  if (op.op === "remove")
    return isRowId(op.id) ? null : { field: "_id", reason: "type" };
  const row = op.row;
  if (!isObject(row)) return { reason: "not_object" };
  for (const [name, value] of Object.entries(row)) {
    if (name === "_id" && !key) {
      if (!isRowId(value)) return { field: name, reason: "type" };
      continue;
    }
    if (name.startsWith("_")) return { field: name, reason: "unknown_field" };
    if (!schema) continue;
    const field = fields.get(name);
    if (!field) return { field: name, reason: "unknown_field" };
    if (!fitsType(field.type, value)) return { field: name, reason: "type" };
  }
  if (key) {
    const missing = key.find((k) => row[k] === undefined || row[k] === null);
    if (missing) return { field: missing, reason: "missing_key" };
    // The id made from the key must stay short enough to name the row in `remove`.
    if ((rowIdOf(schema, row)?.length ?? 0) > DATASET_ID_MAX)
      return { field: key[0], reason: "too_large" };
  } else if (op.op === "upsert" && row._id === undefined) {
    return { field: "_id", reason: "missing_key" };
  }
  const id = rowIdOf(schema, row) ?? (row._id as string | undefined);
  const sized = { ...row, _id: id ?? ID_STAND_IN, _at: AT_STAND_IN };
  return jsonBytes(sized) > DATASET_LIMITS.rowBytes
    ? { reason: "too_large" }
    : null;
}

/**
 * Problems of a schema (and the retention field against it), or null when it
 * is fine. Field names do not start with `_` (those are the dataset's own).
 */
export function schemaProblem(
  schema: DatasetSchema | null,
  retentionField: string | null,
): string | null {
  if (schema) {
    if (!Array.isArray(schema.fields)) return "fields must be a list";
    if (schema.fields.length > DATASET_LIMITS.fields)
      return `at most ${DATASET_LIMITS.fields} fields`;
    const names = new Set<string>();
    for (const f of schema.fields) {
      if (!FIELD_NAME_RE.test(f.name))
        return `field name ${JSON.stringify(f.name)} must be 1 to 64 characters without spaces, not starting with _`;
      if (names.has(f.name)) return `field ${f.name} appears twice`;
      if (!DATASET_FIELD_TYPES.includes(f.type))
        return `field ${f.name} has an unknown type ${String(f.type)}`;
      names.add(f.name);
    }
    for (const k of schema.key ?? [])
      if (!names.has(k)) return `key field ${k} is not a field`;
  }
  if (retentionField) {
    const field = schema?.fields.find((f) => f.name === retentionField);
    if (
      schema &&
      (!field || (field.type !== "date" && field.type !== "datetime"))
    )
      return `retention field ${retentionField} must be a date or datetime field`;
  }
  return null;
}

/**
 * The order rows were written in (`_at`, then `_id`): how a screen's copy and
 * a published page hold them, so a query without `orderBy` answers the same.
 */
export const compareByWrite = (a: DatasetRow, b: DatasetRow): number =>
  a._at < b._at ? -1 : a._at > b._at ? 1 : a._id < b._id ? -1 : 1;

export {
  checkQuery,
  type DatasetQuery,
  type QueryAggregate,
  type QueryBucket,
  type QueryGroup,
  type QueryOp,
  type QueryResult,
  runQuery,
} from "./query";
